import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, MousePointerClick } from "lucide-react";
import { SPINE_REGIONS } from "@/data/spine";
import { CONDITIONS } from "@/data/conditions";
import { SpineSvg } from "./SpineSvg";

const RegionButton = ({ r, on, onPick }) => (
  <button
    type="button"
    onClick={() => onPick(r.id)}
    aria-pressed={on}
    aria-controls="spine-info-panel"
    data-testid={`spine-region-btn-${r.id}`}
    className={`flex min-h-[44px] w-full items-center gap-3 rounded-2xl px-3 py-2 text-left ring-1 transition-[background-color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue ${
      on ? "translate-x-1 ring-transparent" : "bg-white ring-slate-200 hover:bg-slate-50"
    }`}
    style={on ? { backgroundColor: r.soft, boxShadow: `inset 0 0 0 2px ${r.color}` } : undefined}
  >
    <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: r.color }} aria-hidden="true" />
    <span className="min-w-0">
      <span className="block text-sm font-bold leading-tight text-brand-ink sm:text-base">{r.label}</span>
      <span className="block truncate text-xs text-slate-600">{r.segment}</span>
    </span>
  </button>
);

const InfoPanel = ({ r }) => (
  <div id="spine-info-panel" aria-live="polite" className="relative col-span-2 lg:col-span-1 lg:col-start-2" data-testid="spine-info-panel">
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={r.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.22 }}
        className="rounded-2xl p-4"
        style={{ backgroundColor: r.soft }}
      >
        <p className="text-xs font-bold uppercase tracking-[0.14em]" style={{ color: r.ink }} data-testid="spine-info-title">
          {r.label} · {r.segment}
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{r.blurb}</p>
        <p className="mt-3 text-xs font-semibold text-brand-ink">Conditions we help with here</p>
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {r.conditions.map((id) => (
            <li key={id}>
              <Link
                to={`/conditions#${id}`}
                data-testid={`spine-condition-link-${id}`}
                className="inline-flex rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-brand-ink ring-1 ring-black/5 transition-colors hover:text-brand-blue"
              >
                {CONDITIONS.find((c) => c.id === id)?.tile}
              </Link>
            </li>
          ))}
        </ul>
      </motion.div>
    </AnimatePresence>
    <Link to="/conditions" className="text-link mt-3 text-sm" data-testid="spine-all-conditions-link">
      See all conditions <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  </div>
);

export const InteractiveSpine = () => {
  const [active, setActive] = useState("lower");
  const region = SPINE_REGIONS.find((r) => r.id === active);
  return (
    <div className="relative rounded-[2rem] bg-white/85 p-5 shadow-[0_40px_80px_-44px_rgba(1,78,150,0.55)] ring-1 ring-white/80 backdrop-blur-xl sm:p-7" data-testid="spine-widget">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-coralDeep">Interactive spine</p>
          <h2 className="mt-1 font-heading text-2xl font-extrabold tracking-tight text-brand-ink">Where does it hurt?</h2>
        </div>
        <span className="mt-1 inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-warmTint px-2.5 py-1 text-xs font-semibold text-brand-coralDeep">
          <MousePointerClick className="h-3.5 w-3.5" aria-hidden="true" /> Tap a region
        </span>
      </div>
      <div className="mt-5 grid grid-cols-[88px_1fr] gap-x-4 gap-y-4 sm:grid-cols-[120px_1fr] sm:gap-x-6">
        <div className="lg:row-span-2">
          <SpineSvg active={active} onPick={setActive} />
        </div>
        <div className="flex flex-col justify-center gap-2" role="group" aria-label="Spine regions">
          {SPINE_REGIONS.map((r) => <RegionButton key={r.id} r={r} on={r.id === active} onPick={setActive} />)}
        </div>
        <InfoPanel r={region} />
      </div>
    </div>
  );
};
